import { useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertTriangle, Building2, MapPin, Phone, Save, Ticket } from "lucide-react";
import { useToast } from "@/ui/primitives/use-toast";
import {
  LGoButton,
  LGoCard,
  LGoCardHeader,
  LGoCardTitle,
  LGoCardDescription,
  LGoCardContent,
  LGoFormField,
} from "@/ui/branded";
import { FormSection } from "@/ui/patterns";
import {
  businessProfileSchema,
  type BusinessProfileInput,
} from "@/domain/business/schema";
import type { Business } from "@/domain/business/types";
import {
  useCreateBusiness,
  useUpdateBusiness,
} from "@/features/settings/hooks/useBusinessMutations";
import { businessSaveRefusalMessage } from "@/features/settings/business-refusals";
import {
  businessProfileValues,
  hasBusinessProfileEdits,
} from "@/features/settings/business-profile-values";
import {
  businessProfileGaps,
  incompleteProfileNotice,
} from "@/features/settings/business-profile-gaps";
import { DiscountUsageLimitField } from "./DiscountUsageLimitField";
import { LocationSection } from "./LocationSection";

interface BusinessProfileFormProps {
  business: Business | null;
  /** Seeds the notification email the first time a partner fills it in. The
   *  address is copied once, never read live from the account — a login email
   *  is unverified and a business can have more than one account behind it. */
  loginEmail: string | null;
}

export function BusinessProfileForm({ business, loginEmail }: BusinessProfileFormProps) {
  const hasBusiness = !!business;
  const { toast } = useToast();
  const createMutation = useCreateBusiness();
  const updateMutation = useUpdateBusiness();
  const [isMapOpen, setIsMapOpen] = useState(false);
  const showsUnsavedLoginEmail = !!business && !business.notificationEmail && !!loginEmail;
  const profileGaps = business ? businessProfileGaps(business) : [];

  const form = useForm<BusinessProfileInput>({
    resolver: zodResolver(businessProfileSchema),
    mode: "onBlur",
    defaultValues: businessProfileValues(business, loginEmail),
  });

  useEffect(() => {
    if (business) {
      form.reset(businessProfileValues(business, loginEmail));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business?.id]);

  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  /** React Hook Form's own dirty flag compares an emptied "" against the saved
   *  null strictly, so a field typed into and cleared again would claim an edit
   *  forever. A pre-filled login address is the opposite case: a pending change
   *  on an untouched form, because it exists only here until the partner saves
   *  it. */
  const hasSomethingToSave =
    !hasBusiness ||
    hasBusinessProfileEdits(form.formState.defaultValues, form.watch()) ||
    showsUnsavedLoginEmail;

  const onSubmit = async (data: BusinessProfileInput) => {
    try {
      const { sampleSentTo } =
        hasBusiness && business
          ? await updateMutation.mutateAsync({ business, input: data })
          : await createMutation.mutateAsync(data);
      form.reset(data);

      const savedDescription = hasBusiness
        ? "Údaje podniku byly úspěšně aktualizovány."
        : "Váš podnik byl úspěšně vytvořen.";

      toast({
        title: hasBusiness ? "Změny uloženy" : "Podnik vytvořen",
        description: sampleSentTo
          ? `Na adresu ${sampleSentTo} jsme poslali zkušební zprávu. Pokud nedorazí, adresu prosím opravte.`
          : savedDescription,
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Chyba",
        description:
          businessSaveRefusalMessage(error) ??
          (hasBusiness
            ? "Nepodařilo se aktualizovat údaje podniku."
            : "Nepodařilo se vytvořit podnik."),
      });
    }
  };

  return (
    <LGoCard>
      <LGoCardHeader>
        <div className="flex items-start gap-3">
          <Building2 className="h-5 w-5 text-primary mt-1" aria-hidden="true" />
          <div>
            <LGoCardTitle>{hasBusiness ? "Údaje podniku" : "Nový podnik"}</LGoCardTitle>
            <LGoCardDescription>
              {hasBusiness
                ? "Tyto informace se zobrazí uživatelům v aplikaci"
                : "Vyplňte základní informace o vašem podniku"}
            </LGoCardDescription>
          </div>
        </div>
      </LGoCardHeader>
      <LGoCardContent>
        <FormProvider {...form}>
          {/* noValidate: the browser refuses to submit a natively :invalid input
              (e.g. a half-typed email) before RHF runs, so the Czech Zod message
              would never render. Zod does the real validation. */}
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" noValidate>
            {profileGaps.length > 0 && (
              <p
                role="status"
                className="flex items-start gap-2 rounded-md border border-primary/40 bg-accent px-3 py-2 text-xs text-accent-foreground"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <span>{incompleteProfileNotice(profileGaps)}</span>
              </p>
            )}
            <LGoFormField
              name="name"
              label="Název podniku"
              required
              placeholder="např. Fitness Studio Praha"
            />

            <FormSection
              title={
                <span className="inline-flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" /> Adresa
                </span>
              }
              columns={2}
            >
              <div className="md:col-span-2">
                <LGoFormField
                  name="addressStreet"
                  label="Ulice a číslo"
                  required
                  placeholder="např. Vinohradská 123"
                />
              </div>
              <LGoFormField name="addressCity" label="Město" required placeholder="např. Praha" />
              <LGoFormField name="addressCode" label="PSČ" required placeholder="např. 120 00" />
            </FormSection>

            <FormSection
              title={
                <span className="inline-flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" /> Kontakt
                </span>
              }
            >
              <div className="space-y-2">
                <LGoFormField
                  name="notificationEmail"
                  label="E-mail pro potvrzování rezervací"
                  type="email"
                  placeholder="rezervace@podnik.cz"
                  hint="Sem chodí žádosti o potvrzení rezervace."
                />
                {showsUnsavedLoginEmail && (
                  <p
                    role="status"
                    className="flex items-start gap-2 rounded-md border border-primary/40 bg-accent px-3 py-2 text-xs text-accent-foreground"
                  >
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                    <span>
                      Zatím neuložená — předvyplněno z přihlášení. Dokud neuložíte, nic sem
                      neposíláme.
                    </span>
                  </p>
                )}
              </div>

              <LGoFormField
                name="phone"
                label="Telefonní číslo podniku"
                type="tel"
                required
                placeholder="+420 777 123 456"
                hint="Zákazníkům ho zatím nezobrazujeme."
              />
            </FormSection>

            <FormSection
              title={
                <span className="inline-flex items-center gap-2">
                  <Ticket className="h-4 w-4 text-muted-foreground" /> Opakované využití slevy
                </span>
              }
            >
              <DiscountUsageLimitField />
            </FormSection>

            <LocationSection isOpen={isMapOpen} onOpenChange={setIsMapOpen} />

            <div className="sticky bottom-0 z-10 -mx-5 -mb-5 flex flex-wrap items-center gap-3 rounded-b-lg border-t border-border bg-card px-5 py-3">
              <LGoButton
                type="submit"
                variant="primary"
                loading={isSubmitting}
                disabled={!hasSomethingToSave}
                iconLeft={<Save />}
              >
                {hasBusiness ? "Uložit změny" : "Vytvořit podnik"}
              </LGoButton>
              {!hasSomethingToSave && (
                <span className="text-xs text-muted-foreground">Zatím není co uložit.</span>
              )}
            </div>
          </form>
        </FormProvider>
      </LGoCardContent>
    </LGoCard>
  );
}
